// ─────────────────────────────────────────────────────────────────────────────
// Buckly primitives — the few pieces every tab shares.
//
// The tabs are a mechanical port of Buckly.dc.html (scratchpad/sparticus/buckly/
// b1/scripts/dc2tsx.py): every inline style is the design's own CSS text, carried
// verbatim and applied through sx(), which is how the design's runtime applies it.
// Keeping the strings byte-identical is what lets a script prove the port matches
// the design, declaration for declaration.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { FONT_ALIASES } from './tokens';
import bucklyMark from './assets/buckly-mark.png';

export const ASSET: Record<string, string> = {
  'buckly-mark.png': bucklyMark,
};

// ── sx: the design's CSS text → a React style object ─────────────────────────
// Declarations split on ';' outside parentheses; a repeated property keeps the
// LAST value, exactly as CSS does (the design repeats `color` in several spans).
const cache = new Map<string, React.CSSProperties>();

/** The design's font-family value, pointed at Buckly's own registered faces. */
export function fam(value: string): string {
  for (const [from, to] of FONT_ALIASES) value = value.split(from).join(to);
  return value;
}

function camel(prop: string): string {
  const p = prop.startsWith('-webkit-') ? 'Webkit-' + prop.slice(8) : prop;
  return p.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

export function sx(css: string): React.CSSProperties {
  const hit = cache.get(css);
  if (hit) return hit;
  const out: Record<string, string> = {};
  let depth = 0;
  let start = 0;
  const decls: string[] = [];
  for (let i = 0; i < css.length; i++) {
    const c = css[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ';' && depth === 0) { decls.push(css.slice(start, i)); start = i + 1; }
  }
  decls.push(css.slice(start));
  for (const d of decls) {
    const i = d.indexOf(':');
    if (i < 0) continue;
    const prop = d.slice(0, i).trim();
    let value = d.slice(i + 1).trim();
    if (!prop || !value) continue;
    if (prop === 'font-family') value = fam(value);
    out[camel(prop)] = value;
  }
  cache.set(css, out as React.CSSProperties);
  return out as React.CSSProperties;
}

// ── Hover: the design's `style-hover` ─────────────────────────────────────────
type HoverProps = {
  as: 'button';
  style: React.CSSProperties;
  hover: React.CSSProperties;
  children?: React.ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'>;

export function Hover({ as, style, hover, children, ...rest }: HoverProps) {
  const [on, setOn] = useState(false);
  return React.createElement(as, {
    ...rest,
    style: on ? { ...style, ...hover } : style,
    onMouseEnter: () => setOn(true),
    onMouseLeave: () => setOn(false),
  }, children);
}

// ── INERT (owner ruling U8, 27 Oct) ──────────────────────────────────────────
// Every control that would save, send, start, confirm, reject, pass, discard or
// tick is drawn exactly as designed and does NOTHING: no state change, no
// request, no success shown. Her memory is not in yet (B-2 / B-4 wire them).
export const INERT = (): void => {};

// ── The design's "Designed next" card (Buckly.dc.html:941-947) ───────────────
// Review's This Week / This Month are not drawn yet (U12): the design shows this.
export function designPlaceholder(title: string, note: string) {
  return React.createElement('div', { style: { minHeight: '380px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', borderRadius: '14px', background: 'linear-gradient(180deg, rgba(10,16,28,0.6), rgba(6,9,18,0.72))', border: '1px solid rgba(60,150,230,0.16)' } },
    React.createElement('div', { style: { width: '54px', height: '54px', borderRadius: '50%', border: '1.5px solid rgba(60,150,230,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 16px rgba(40,110,200,0.14)' } },
      React.createElement('span', { style: { width: '10px', height: '10px', borderRadius: '50%', background: '#3c96e6', boxShadow: '0 0 12px rgba(60,150,230,0.7)' } })),
    React.createElement('div', { style: { fontFamily: fam("'JetBrains Mono',monospace"), fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(95,184,240,0.75)' } }, title),
    React.createElement('div', { style: { fontSize: '15px', color: 'rgba(210,220,222,0.5)' } }, note));
}

// ── Page state ───────────────────────────────────────────────────────────────
// The design's component state, field for field (Buckly.dc.html:922). It lives
// on the page, as it does in the design, so a sub-tab keeps its place when you
// leave the tab and come back; Reset returns only the TAB to Today (:1283).
export interface BucklyState {
  tab: string; patSub: string; revSub: string; sessType: number; agendaOn: boolean[];
  phase: number; call: boolean; typeMode: boolean; draft: string;
  extra: [string, string, string][]; dream: boolean; histOpen: number; dSub: string;
  folded: Record<string, boolean>; cand: Record<string, string>; weekIdx: number;
  monthIdx: number; kmOpen: number; nightOpen: number;
}

export type SetBuckly = (patch: Partial<BucklyState> | ((s: BucklyState) => Partial<BucklyState>)) => void;

export interface TabProps { s: BucklyState; set: SetBuckly }
